import {
  Company,
  Credit,
  FinanceRate,
  Job,
  Payment,
} from '@kanda-libs/ks-frontend-services';

import { getLoanInfo } from 'common/finance/helpers';
import { ORDER, SORTING } from 'components/job/JobTable/constants';
import {
  FormattedTableJob,
  JobWithCompanyAndCredit,
} from 'components/job/JobTable/types';
import get from 'lodash.get';

import { getFinanceOptionDetails } from '../JobsListPage/JobListPageActions/DownloadCsvButton/helpers';

export const sortByName = (
  data: Job[],
  accessor: string,
  order: string,
): Job[] =>
  data.sort((data1, data2) => {
    const customerName =
      [data1.customer?.first_name, data1.customer?.last_name].join(' ') || '';
    const customerName2 =
      [data2.customer?.first_name, data2.customer?.last_name].join(' ') || '';

    if (order === ORDER.INVERSE)
      return customerName2.localeCompare(customerName);

    return customerName.localeCompare(customerName2);
  });

export const sortByPrice = (data: Job[], order: string) =>
  data.sort((data1, data2) => {
    const data1Price =
      (get(data1, 'total.amount_vat_inclusive.amount') as number) || 0;
    const data2Price =
      (get(data2, 'total.amount_vat_inclusive.amount') as number) || 0;

    if (order === ORDER.INVERSE)
      return new Date(data2Price).getTime() - new Date(data1Price).getTime();
    return new Date(data1Price).getTime() - new Date(data2Price).getTime();
  });

export const sortByUpdatedAt = (data: Job[], order: string) =>
  data.sort((data1, data2) => {
    const data1UpdatedAt =
      (get(data1, 'metadata.updated_at') as unknown as string) || '';
    const data2UpdatedAt =
      (get(data2, 'metadata.updated_at') as unknown as string) || '';

    if (order === ORDER.INVERSE)
      return (
        new Date(data1UpdatedAt).getTime() - new Date(data2UpdatedAt).getTime()
      );

    return (
      new Date(data2UpdatedAt).getTime() - new Date(data1UpdatedAt).getTime()
    );
  });

export const sortByCreatedAt = (data: Job[], order: string) =>
  data.sort((data1, data2) => {
    const data1CreatedAt =
      (get(data1, 'metadata.created_at') as unknown as string) || '';
    const data2CreatedAt =
      (get(data2, 'metadata.created_at') as unknown as string) || '';

    if (order === ORDER.INVERSE)
      return (
        new Date(data1CreatedAt).getTime() - new Date(data2CreatedAt).getTime()
      );

    return (
      new Date(data2CreatedAt).getTime() - new Date(data1CreatedAt).getTime()
    );
  });

// Sorts the data according to sorting and order
export const sortData = (
  data?: Job[],
  sorting?: string,
  order: string = '',
) => {
  if (!data) return null;

  // If no jobs, return null to show the placeholder
  if (data.length === 0) return [];

  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.UPDATED_AT) return sortByUpdatedAt(data, order);
  // If sorting is created_at, return data sorted by updated at date
  if (sorting === SORTING.CREATED_AT) return sortByCreatedAt(data, order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.JOB) return sortByName(data, 'title', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.ID) return sortByName(data, 'id', order);

  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.PRICE) return sortByPrice(data, order);

  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.CUSTOMER)
    return sortByName(data, 'customerDetails.name', order);

  return data;
};

export const filterJobs = (
  jobs: JobWithCompanyAndCredit[] | null = [],
  solar = false,
  satNoteSigned = false,
  pendingPayout = false,
  companies: Company[],
  credits: Credit[],
  cid?: string,
): JobWithCompanyAndCredit[] | undefined => {
  if (!jobs) return [];

  let filteredJobs = jobs;

  if (cid) {
    filteredJobs = filteredJobs.filter((job) => job.cid === cid);
  }

  if (pendingPayout) {
    filteredJobs = formatJobsPendingPayout(jobs, companies, credits, solar);
  }

  if (solar) {
    filteredJobs = filteredJobs.filter((job) => job.job_type === 'solar');
  }

  if (satNoteSigned) {
    filteredJobs = filteredJobs
      .filter((job) =>
        solar ? job?.job_type === 'solar' : job?.job_type !== 'solar',
      )
      .filter(
        (job) =>
          ['sat_note_signed', 'sat_note_delayed'].indexOf(
            job?.finance_status || '',
          ) !== -1,
      )
      .filter((job) => job?.archived !== 'yes');
  }

  return filteredJobs.filter((job) => job.flow_type !== 'example');
};

export const formatJobsPendingPayout = (
  jobs?: Job[],
  companies?: Company[],
  credits?: Credit[],
  includeSolar = false,
): JobWithCompanyAndCredit[] => {
  if (!jobs || !companies || !credits) return [];
  return jobs?.reduce((pending, job) => {
    if (!job?.finance_status) return pending;
    if (!includeSolar && job?.job_type === 'solar') return pending;
    if (job?.finance_status !== 'payout_pending') return pending;
    const company = companies.filter((cmp) => cmp?.id === job?.cid)?.[0];
    const loanId = job?.payments
      ?.filter((pmt) => pmt?.payment_option?.payment_method === 'loan')
      .sort((p1, p2) => {
        const p1Date = p1.metadata?.created_at as unknown as number;
        const p2Date = p2.metadata?.created_at as unknown as number;

        return (
          (new Date(p2Date) as unknown as number) -
          (new Date(p1Date) as unknown as number)
        );
      })?.[0]?.id;
    const credit = credits.filter((crd) => crd?.id === loanId)?.[0];
    if (!company) return pending;
    if (!credit) return pending;
    pending.push({
      ...job,
      company,
      credit,
    });
    return pending;
  }, [] as JobWithCompanyAndCredit[]);
};

const getPendingLoanPayment = (payments: Payment[] = []): Payment | null => {
  if (!payments) return null;
  const loans = payments
    .filter((payment) => payment?.payment_option?.payment_method === 'loan')
    .filter((loan) => loan?.status === 'paid')
    .sort(
      (loan1, loan2) =>
        (new Date(
          loan2?.metadata?.updated_at as unknown as number,
        ) as unknown as number) -
        (new Date(
          loan1?.metadata?.updated_at as unknown as number,
        ) as unknown as number),
    );
  return loans[0];
};

const getLoanDeposit = (
  payments?: Payment[],
  depositValue: number = 0,
): Payment | null => {
  if (!payments) return null;
  const loans = payments
    .filter((payment) => payment?.payment_option?.payment_method !== 'loan')
    .filter(
      (deposit) => deposit?.payment_option?.amount?.amount === depositValue,
    )
    .sort((deposit1, deposit2) => {
      const deposit1Date = deposit1?.metadata?.updated_at as unknown as number;
      const deposit2Date = deposit2?.metadata?.updated_at as unknown as number;

      return (
        (new Date(deposit2Date) as unknown as number) -
        (new Date(deposit1Date) as unknown as number)
      );
    });

  return loans[0];
};

export interface FormattedPaymentInfo {
  reference?: string;
  loan?: number;
  deposit?: number;
  depositTaken?: string;
  apr?: number;
  term?: number;
  rate?: FinanceRate;
  subsidyPercentage?: number;
  baseFee?: number;
  toPay?: number;
}

export const formatPaymentInfo = (
  job: FormattedTableJob,
): FormattedPaymentInfo => {
  const loan = getPendingLoanPayment(job?.payments);

  const { depositValue, depositType, rate } = job;
  const deposit = getLoanDeposit(job?.payments, depositValue);
  const depositTaken = depositType !== 'no_deposit';

  const { interest: apr, duration: term } = getFinanceOptionDetails(rate);

  const loanAmount = loan?.payment_option?.amount?.amount || 0;
  const depositAmount = deposit?.payment_option?.amount?.amount || 0;

  const details = getLoanInfo(job?.credit, job?.company, depositTaken, false);

  return {
    reference: loan?.id || '',
    loan: loanAmount,
    deposit: depositAmount,
    depositTaken: depositTaken ? 'True' : 'False',
    rate,
    apr,
    term,
    subsidyPercentage: (details.subsidyPercentage as unknown as number) * 100,
    baseFee: (details.subsidyFee as unknown as number) * 100,
    toPay: (details.amountToPay as unknown as number) * 100,
  };
};

export const formatJobs = (
  jobs?: JobWithCompanyAndCredit[],
  companies: Company[] = [],
): FormattedTableJob[] => {
  if (!jobs) return [];

  return jobs.map((job) => {
    const company = companies.find((value) => value?.id === job?.cid);

    const formattedJob: FormattedTableJob = {
      id: job?.id || '',
      title: job?.title || '',
      cid: job?.cid || '',
      bankDetails: {
        name: company?.bank_account?.account_name,
        number: company?.bank_account?.account_number,
        sortCode: company?.bank_account?.sort_code,
      },
      description: job?.description || '',
      financeStatus: job?.finance_status || '',
      quotedTo: job?.quoted_to || '',
      payments: job?.payments || [],
      status: job?.status || '',
      price: job?.total?.amount_vat_inclusive?.amount || 0,
      archived: job?.archived || 'no',
      createdAt: job?.metadata?.created_at || '',
      updatedAt: job?.metadata?.updated_at || '',
      companyName:
        company?.limited_company_info?.company_name ||
        company?.sole_trader_info?.trading_name ||
        '',
      companyContactEmail: company?.contact_info?.contact_email || '',
      companyContactPhone: company?.contact_info?.contact_phone || '',
      rate: job?.customer_options?.finance_option,
      rateName: job?.customer_options?.finance_option?.name || 'no rate chosen',
      depositValue:
        job?.deposit_value?.amount ||
        job?.customer_options?.deposit_value?.amount ||
        0,
      depositType: job?.deposit_type,
      customerDetails: {
        firstName: job?.customer?.first_name || '',
        lastName: job?.customer?.last_name || '',
        name: `${job?.customer?.first_name || ''} ${
          job?.customer?.last_name || ''
        }`,
        email: job?.customer?.email || '',
        phone: job?.customer?.phone || '',
        postCode: job?.customer?.address?.postcode || '',
        addressLine1: job?.customer?.address?.line_1 || '',
        addressLine2: job?.customer?.address?.line_2 || '',
        city: job?.customer?.address?.city || '',
      },
      credit: job?.credit,
      company,
    };

    return {
      ...formattedJob,
      ...formatPaymentInfo(formattedJob),
    };
  });
};
